import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Download, Play, FileText, Search, ExternalLink, AlertTriangle, GraduationCap, Video, BookOpen, Users, Image, Filter } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import GoogleScholarSearch from '../components/GoogleScholarSearch';

const Resources = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFilter, setSelectedFilter] = useState('all');
  const { toast } = useToast();

  // Resource categories and items
  const resourceCategories = {
    scientific: {
      title: "Scientific & Conservation Info",
      icon: GraduationCap,
      color: "blue",
      items: [
        {
          title: "NOAA Fisheries – Vaquita Profile",
          description: "Detailed biology, threats, conservation status from U.S. government marine experts",
          type: "read",
          url: "https://www.fisheries.noaa.gov/species/vaquita",
          category: "scientific"
        },
        {
          title: "IUCN Red List – Vaquita",
          description: "Official endangered species listing and population estimates",
          type: "read",
          url: "https://www.iucnredlist.org/species/17028/50370296",
          category: "scientific"
        },
        {
          title: "WWF – Vaquita Facts",
          description: "Public-friendly overview of threats and current conservation efforts",
          type: "read",
          url: "https://www.worldwildlife.org/species/vaquita",
          category: "scientific"
        },
        {
          title: "Porpoise Conservation Society",
          description: "Comprehensive vaquita research and timeline data",
          type: "read",
          url: "https://porpoise.org/about-porpoises/vaquita/",
          category: "scientific"
        }
      ]
    },
    media: {
      title: "Educational Media",
      icon: Video,
      color: "purple",
      items: [
        {
          title: "Sea of Shadows – National Geographic",
          description: "Award-winning documentary about vaquita conservation and illegal totoaba trade",
          type: "watch",
          url: "https://www.nationalgeographic.com/tv/shows/sea-of-shadows",
          category: "media"
        },
        {
          title: "Vaquita: Last Chance for the Desert Porpoise",
          description: "Documentary exploring the critical situation of the vaquita",
          type: "watch",
          url: "https://youtube.com/results?search_query=vaquita+last+chance+desert+porpoise",
          category: "media"
        },
        {
          title: "NOAA's 'What is a Vaquita?' Video",
          description: "Short educational video explaining vaquita biology and conservation",
          type: "watch",
          url: "https://youtube.com/results?search_query=NOAA+what+is+vaquita",
          category: "media"
        },
        {
          title: "Marine Conservation Podcasts",
          description: "Podcast episodes featuring vaquita experts and researchers",
          type: "watch",
          url: "https://podcasts.google.com/search/vaquita%20marine%20conservation",
          category: "media"
        }
      ]
    },
    publications: {
      title: "Reports & Publications",
      icon: FileText,
      color: "green",
      items: [
        {
          title: "CIRVA Reports",
          description: "International Committee for the Recovery of the Vaquita official reports",
          type: "read",
          url: "https://www.porpoise.org/what-we-do/research/",
          category: "publications"
        },
        {
          title: "Google Scholar – Vaquita Research",
          description: "Academic papers and scientific publications about vaquita conservation",
          type: "read",
          url: "https://scholar.google.com/scholar?q=vaquita+phocoena+sinus+conservation",
          category: "publications"
        },
        {
          title: "Sea Shepherd Conservation Updates",
          description: "Latest reports from Operation Milagro vaquita protection campaign",
          type: "read",
          url: "https://www.seashepherdglobal.org/campaigns/operation-milagro/",
          category: "publications"
        },
        {
          title: "WWF Conservation Reports",
          description: "World Wildlife Fund reports on vaquita conservation progress",
          type: "read",
          url: "https://www.worldwildlife.org/species/vaquita",
          category: "publications"
        }
      ]
    },
    teaching: {
      title: "Teaching & Awareness Materials",
      icon: Users,
      color: "orange",
      items: [
        {
          title: "Vaquita Fact Sheet",
          description: "Downloadable fact sheet with key vaquita information and statistics",
          type: "download",
          url: "#",
          category: "teaching"
        },
        {
          title: "Gillnet Impact Infographic",
          description: "Visual explanation of how gillnets work and their impact on vaquitas",
          type: "download",
          url: "#",
          category: "teaching"
        },
        {
          title: "School Presentation Kit",
          description: "Complete slide deck for educational presentations about vaquita conservation",
          type: "download",
          url: "#",
          category: "teaching"
        },
        {
          title: "Vaquita Habitat Map",
          description: "Detailed map showing vaquita habitat and fishing zones",
          type: "download",
          url: "#",
          category: "teaching"
        },
        {
          title: "Vaquita vs. Other Cetaceans Chart",
          description: "Size comparison infographic showing vaquitas relative to other marine mammals",
          type: "download",
          url: "#",
          category: "teaching"
        }
      ]
    },
    collections: {
      title: "Resource Collections",
      icon: BookOpen,
      color: "indigo",
      items: [
        {
          title: "Monterey Bay Aquarium – Seafood Watch",
          description: "Sustainable seafood guide to help reduce bycatch impact",
          type: "read",
          url: "https://www.seafoodwatch.org/",
          category: "collections"
        },
        {
          title: "Marine Mammal Commission",
          description: "Learning hub for marine mammal conservation and research",
          type: "read",
          url: "https://www.mmc.gov/",
          category: "collections"
        },
        {
          title: "Wikimedia Commons – Vaquita Images",
          description: "Public domain vaquita images for educational and awareness projects",
          type: "download",
          url: "https://commons.wikimedia.org/wiki/Category:Vaquita",
          category: "collections"
        },
        {
          title: "Ocean Conservancy Resources",
          description: "Marine conservation database and educational materials",
          type: "read",
          url: "https://oceanconservancy.org/",
          category: "collections"
        }
      ]
    }
  };

  // Get all resources for filtering
  const allResources = Object.values(resourceCategories).flatMap(category => 
    category.items.map(item => ({ ...item, categoryInfo: category }))
  );

  // Filter resources based on search term and selected filter
  const filteredResources = allResources.filter(resource => {
    const matchesSearch = resource.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         resource.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = selectedFilter === 'all' || resource.type === selectedFilter;
    return matchesSearch && matchesFilter;
  });

  const handleResourceClick = (url, title, type) => {
    if (url === '#') {
      toast({
        title: "Coming Soon!",
        description: `${title} will be available for download soon.`,
        duration: 5000,
      });
    } else {
      window.open(url, '_blank', 'noopener noreferrer');
    }
  };

  const filterOptions = [
    { value: 'all', label: 'All Resources', icon: BookOpen },
    { value: 'watch', label: 'Watch', icon: Video },
    { value: 'read', label: 'Read', icon: FileText },
    { value: 'download', label: 'Download', icon: Download }
  ];

  return (
    <div className="pt-20 bg-gray-900">
      {/* Critical Alert Banner */}
      <div className="bg-red-800 text-white py-3 sticky top-20 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center text-center text-sm sm:text-base">
            <AlertTriangle className="h-4 w-4 mr-2 flex-shrink-0" />
            <p className="font-semibold">
              CRITICAL: Fewer than 10 vaquitas remain. Extinction expected within months without immediate action.
            </p>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <section className="min-h-screen bg-gray-900 text-white relative overflow-hidden">
        {/* Educational Background */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-15"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1481627834876-b7833e8f5570?w=1200&h=800&fit=crop&crop=center')`,
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-gray-900/90 via-gray-900/80 to-gray-900/60"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold mb-8 leading-tight">
              Educational
              <span className="block text-purple-400">Resources</span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 mb-8 leading-relaxed max-w-4xl mx-auto">
              Comprehensive collection of scientific research, educational materials, and conservation resources 
              about the vaquita porpoise. Everything you need to learn, teach, and take action.
            </p>
          </div>

          {/* Search and Filter Interface - Rainforest Inspired */}
          <div className="relative">
            {/* Background with rainforest-inspired gradient */}
            <div className="bg-gradient-to-br from-green-900/30 via-gray-800/50 to-blue-900/30 backdrop-blur border-2 border-green-500/30 rounded-2xl p-8 mb-16 relative overflow-hidden">
              {/* Animated background elements */}
              <div className="absolute top-0 right-0 w-32 h-32 bg-green-400/10 rounded-full blur-2xl animate-pulse"></div>
              <div className="absolute bottom-0 left-0 w-32 h-32 bg-blue-400/10 rounded-full blur-2xl animate-pulse" style={{animationDelay: '1s'}}></div>
              
              <div className="relative z-10">
                <div className="text-center mb-8">
                  <div className="inline-flex items-center space-x-3 mb-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-700 rounded-full flex items-center justify-center shadow-lg">
                      <Search className="h-6 w-6 text-white" />
                    </div>
                    <h3 className="text-2xl font-bold text-white">Find Resources</h3>
                  </div>
                  <p className="text-green-200 leading-relaxed">
                    Search through our comprehensive collection of vaquita research and educational materials
                  </p>
                </div>

                <div className="space-y-6">
                  {/* Enhanced Search Bar */}
                  <div className="relative group">
                    <div className="absolute inset-0 bg-gradient-to-r from-green-500/20 to-blue-500/20 rounded-xl blur-sm group-focus-within:blur-md transition-all duration-300"></div>
                    <div className="relative">
                      <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-green-400 h-5 w-5 z-10" />
                      <Input
                        placeholder="Search resources by title, description, or content type..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-12 h-16 bg-gray-900/80 border-2 border-green-500/30 text-white placeholder-gray-400 text-lg rounded-xl focus:border-green-400/50 focus:ring-2 focus:ring-green-400/20 backdrop-blur transition-all duration-300"
                      />
                    </div>
                  </div>
                  
                  {/* Enhanced Filter Buttons - Rainforest Style */}
                  <div className="flex flex-wrap gap-4 justify-center">
                    <div className="flex items-center space-x-3 text-green-200 text-sm font-medium mb-2 w-full justify-center">
                      <Filter className="h-4 w-4" />
                      <span>Filter by type:</span>
                    </div>
                    <div className="flex flex-wrap gap-3 justify-center">
                      {filterOptions.map((option) => {
                        const IconComponent = option.icon;
                        const isActive = selectedFilter === option.value;
                        return (
                          <Button
                            key={option.value}
                            variant="outline"
                            size="sm"
                            onClick={() => setSelectedFilter(option.value)}
                            className={`group relative overflow-hidden rounded-full px-6 py-3 transition-all duration-300 ${
                              isActive 
                                ? 'bg-gradient-to-r from-green-600 to-green-700 border-green-500 text-white shadow-lg shadow-green-500/20' 
                                : 'border-2 border-green-600/30 text-green-300 hover:border-green-500/50 hover:bg-green-600/10 backdrop-blur'
                            }`}
                          >
                            <div className="absolute inset-0 bg-gradient-to-r from-white/5 to-white/10 translate-y-full group-hover:translate-y-0 transition-transform duration-300"></div>
                            <div className="relative flex items-center gap-2">
                              <IconComponent className="h-4 w-4" />
                              <span className="font-medium">{option.label}</span>
                            </div>
                          </Button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Search Stats */}
                  {(searchTerm !== '' || selectedFilter !== 'all') && (
                    <div className="text-center">
                      <div className="inline-flex items-center space-x-2 bg-green-900/30 border border-green-500/30 rounded-full px-4 py-2">
                        <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                        <span className="text-green-300 text-sm">
                          {filteredResources.length} result{filteredResources.length !== 1 ? 's' : ''} found
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Search Results Section - Show prominently when searching */}
      {(searchTerm !== '' || selectedFilter !== 'all') && (
        <section className="py-12 bg-gradient-to-b from-green-900/20 to-gray-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-8">
              <div className="inline-flex items-center space-x-3 mb-4">
                <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center">
                  <Search className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="text-2xl md:text-3xl font-bold text-white">
                    Search Results
                  </h3>
                  <p className="text-green-300">
                    {filteredResources.length} resource{filteredResources.length !== 1 ? 's' : ''} found
                    {searchTerm && ` for "${searchTerm}"`}
                    {selectedFilter !== 'all' && ` in "${selectedFilter}" category`}
                  </p>
                </div>
              </div>
            </div>

            {filteredResources.length === 0 ? (
              <div className="text-center bg-gray-800/50 border border-gray-700 rounded-lg p-12">
                <Search className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                <h4 className="text-xl font-bold text-white mb-4">No Results Found</h4>
                <p className="text-gray-400 mb-6">
                  We couldn't find any resources matching your search criteria.
                </p>
                <Button 
                  onClick={() => {
                    setSearchTerm('');
                    setSelectedFilter('all');
                  }}
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  Clear All Filters
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredResources.map((resource, index) => (
                  <div key={index} className="group">
                    <div className="bg-gray-800/70 backdrop-blur border-2 border-green-500/30 rounded-xl p-6 hover:bg-gray-700/70 hover:border-green-400/50 transition-all duration-300 hover:-translate-y-2 h-full flex flex-col">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center space-x-3">
                          {resource.type === 'watch' && <Video className="h-5 w-5 text-purple-400" />}
                          {resource.type === 'read' && <FileText className="h-5 w-5 text-blue-400" />}
                          {resource.type === 'download' && <Download className="h-5 w-5 text-green-400" />}
                          <span className={`px-3 py-1 rounded-full text-xs font-medium capitalize ${
                            resource.type === 'watch' ? 'bg-purple-800/50 text-purple-200 border border-purple-600/30' :
                            resource.type === 'read' ? 'bg-blue-800/50 text-blue-200 border border-blue-600/30' :
                            'bg-green-800/50 text-green-200 border border-green-600/30'
                          }`}>
                            {resource.type}
                          </span>
                        </div>
                        <ExternalLink className="h-4 w-4 text-gray-400 group-hover:text-green-400 transition-colors" />
                      </div>
                      
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center mb-4 ${
                        resource.categoryInfo.color === 'blue' ? 'bg-blue-600' :
                        resource.categoryInfo.color === 'purple' ? 'bg-purple-600' :
                        resource.categoryInfo.color === 'green' ? 'bg-green-600' :
                        resource.categoryInfo.color === 'orange' ? 'bg-orange-600' :
                        'bg-indigo-600'
                      }`}>
                        <resource.categoryInfo.icon className="h-6 w-6 text-white" />
                      </div>
                      
                      <h3 className="text-lg font-bold text-white mb-3 leading-tight">
                        {resource.title}
                      </h3>
                      
                      <p className="text-gray-300 text-sm leading-relaxed mb-4 flex-grow">
                        {resource.description}
                      </p>
                      
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-500 capitalize">
                          {resource.categoryInfo.title}
                        </span>
                        <Button 
                          onClick={() => handleResourceClick(resource.url, resource.title, resource.type)}
                          size="sm"
                          className={`${
                            resource.type === 'watch' ? 'bg-purple-600 hover:bg-purple-700' :
                            resource.type === 'read' ? 'bg-blue-600 hover:bg-blue-700' :
                            'bg-green-600 hover:bg-green-700'
                          } text-white transition-colors duration-200`}
                        >
                          {resource.type === 'watch' && <Play className="h-4 w-4 mr-2" />}
                          {resource.type === 'read' && <ExternalLink className="h-4 w-4 mr-2" />}
                          {resource.type === 'download' && <Download className="h-4 w-4 mr-2" />}
                          {resource.type === 'watch' ? 'Watch' : 
                           resource.type === 'read' ? 'Read' : 
                           'Download'}
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Quick Actions */}
            <div className="mt-8 text-center">
              <div className="flex flex-wrap gap-3 justify-center">
                <Button 
                  onClick={() => {
                    setSearchTerm('');
                    setSelectedFilter('all');
                  }}
                  variant="outline"
                  className="border-green-600 text-green-400 hover:bg-green-600 hover:text-white"
                >
                  Clear Search
                </Button>
                <Button 
                  onClick={() => window.open('https://scholar.google.com/scholar?q=vaquita+phocoena+sinus+conservation', '_blank')}
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  <GraduationCap className="h-4 w-4 mr-2" />
                  Search More Research
                </Button>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Google Scholar Search Section */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <GoogleScholarSearch />
        </div>
      </section>

      {/* Resource Categories */}
      {Object.entries(resourceCategories).map(([key, category]) => {
        const categoryResources = category.items.filter(item => {
          const matchesSearch = searchTerm === '' || 
            item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
            item.description.toLowerCase().includes(searchTerm.toLowerCase());
          const matchesFilter = selectedFilter === 'all' || item.type === selectedFilter;
          return matchesSearch && matchesFilter;
        });

        // Hide categories when filtering unless they have results
        if ((searchTerm !== '' || selectedFilter !== 'all') && categoryResources.length === 0) {
          return null;
        }

        const IconComponent = category.icon;
        
        return (
          <section key={key} className="py-16 bg-gray-800 relative overflow-hidden">
            {/* Background Pattern Inspired by Rainforest Design */}
            <div className="absolute inset-0 opacity-5">
              <div className="absolute top-0 right-0 w-96 h-96 bg-green-500 rounded-full blur-3xl"></div>
              <div className="absolute bottom-0 left-0 w-96 h-96 bg-blue-500 rounded-full blur-3xl"></div>
            </div>
            
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
              <div className="text-center mb-12">
                {/* Rainforest-inspired circular icon design */}
                <div className="relative inline-block mb-6">
                  <div className={`w-20 h-20 bg-gradient-to-br from-${category.color}-500 to-${category.color}-700 rounded-full flex items-center justify-center mx-auto shadow-lg border-4 border-white/20`}>
                    <IconComponent className="h-10 w-10 text-white" />
                  </div>
                  <div className="absolute -inset-1 bg-gradient-to-br from-green-400/20 to-blue-400/20 rounded-full blur-sm"></div>
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                  {category.title}
                </h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {categoryResources.map((item, index) => (
                  <div key={index} className="group">
                    <div className="bg-gradient-to-br from-gray-900/50 to-gray-800/30 backdrop-blur border border-gray-700/50 rounded-xl p-6 hover:bg-gray-700/50 hover:border-green-500/50 transition-all duration-500 hover:-translate-y-3 hover:shadow-2xl hover:shadow-green-500/10 h-full flex flex-col">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center space-x-3">
                          {/* Type indicator with rainforest-inspired styling */}
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                            item.type === 'watch' ? 'bg-purple-600/20 border border-purple-500/30' :
                            item.type === 'read' ? 'bg-blue-600/20 border border-blue-500/30' :
                            'bg-green-600/20 border border-green-500/30'
                          }`}>
                            {item.type === 'watch' && <Video className="h-4 w-4 text-purple-400" />}
                            {item.type === 'read' && <FileText className="h-4 w-4 text-blue-400" />}
                            {item.type === 'download' && <Download className="h-4 w-4 text-green-400" />}
                          </div>
                          <span className={`px-3 py-1 rounded-full text-xs font-medium capitalize border ${
                            item.type === 'watch' ? 'bg-purple-800/30 text-purple-200 border-purple-600/30' :
                            item.type === 'read' ? 'bg-blue-800/30 text-blue-200 border-blue-600/30' :
                            'bg-green-800/30 text-green-200 border-green-600/30'
                          }`}>
                            {item.type}
                          </span>
                        </div>
                        <ExternalLink className="h-4 w-4 text-gray-400 group-hover:text-green-400 transition-colors duration-300" />
                      </div>
                      
                      <h3 className="text-xl font-bold text-white mb-3 leading-tight group-hover:text-green-300 transition-colors duration-300">
                        {item.title}
                      </h3>
                      
                      <p className="text-gray-300 text-sm leading-relaxed mb-6 flex-grow">
                        {item.description}
                      </p>
                      
                      {/* Rainforest-inspired button design */}
                      <Button 
                        onClick={() => handleResourceClick(item.url, item.title, item.type)}
                        className={`w-full relative overflow-hidden group/btn ${
                          item.type === 'watch' ? 'bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800' :
                          item.type === 'read' ? 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800' :
                          'bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800'
                        } text-white border-0 transition-all duration-300 hover:shadow-lg`}
                      >
                        <div className="absolute inset-0 bg-white/10 translate-y-full group-hover/btn:translate-y-0 transition-transform duration-300"></div>
                        <div className="relative flex items-center justify-center">
                          {item.type === 'watch' && <Play className="h-4 w-4 mr-2" />}
                          {item.type === 'read' && <ExternalLink className="h-4 w-4 mr-2" />}
                          {item.type === 'download' && <Download className="h-4 w-4 mr-2" />}
                          {item.type === 'watch' ? 'Watch Now' : 
                           item.type === 'read' ? 'Read More' : 
                           'Download'}
                        </div>
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        );
      })}

      {/* Call to Action - Rainforest inspired */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-green-900 via-blue-900 to-purple-900"></div>
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-green-400 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute top-3/4 right-1/4 w-64 h-64 bg-blue-400 rounded-full blur-3xl animate-pulse" style={{animationDelay: '1s'}}></div>
        </div>
        
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <div className="bg-white/5 backdrop-blur border border-white/20 rounded-2xl p-12">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-8">
              Spread the Knowledge
            </h2>
            <p className="text-lg md:text-xl text-white/90 mb-10 leading-relaxed">
              These resources are only as powerful as the people who use them. Share this knowledge, 
              educate others, and help build the global awareness needed to save the vaquita.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <Button 
                size="lg" 
                onClick={() => window.open('https://scholar.google.com/scholar?q=vaquita+phocoena+sinus+conservation', '_blank')}
                className="bg-white text-purple-900 hover:bg-gray-100 font-bold px-8 md:px-12 py-4 text-lg md:text-xl rounded-xl shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-1"
              >
                <GraduationCap className="mr-3 h-5 md:h-6 w-5 md:w-6" />
                Start Researching
              </Button>
              <Button 
                size="lg" 
                onClick={() => {
                  if (navigator.share) {
                    navigator.share({
                      title: 'Fight for Vaquita - Educational Resources',
                      text: 'Comprehensive collection of vaquita conservation resources, research, and educational materials.',
                      url: window.location.href
                    }).catch(console.error);
                  } else {
                    navigator.clipboard.writeText(window.location.href).then(() => {
                      toast({
                        title: "Link Copied!",
                        description: "Resource page URL copied to clipboard. Share it anywhere!",
                        duration: 5000,
                      });
                    });
                  }
                }}
                variant="outline" 
                className="border-2 border-white text-white hover:bg-white hover:text-purple-900 font-bold px-8 md:px-12 py-4 text-lg md:text-xl rounded-xl bg-transparent backdrop-blur transition-all duration-300 hover:-translate-y-1"
              >
                <ExternalLink className="mr-3 h-5 md:h-6 w-5 md:w-6" />
                Share This Page
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Resources;